'use client';

import { motion } from 'motion/react';
import React from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import RichText from '@/components/RichText';
import type { AccordionOnMediaBlock as AccordionOnMediaBlockProps } from '@/payload-types';
import { cn } from '@/utils/cn';

import { AccordionBlock } from '../AccordionBlock/Component';
import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';

// Variants for the fade-in animation
const fadeInVariant = (dependsOn: boolean) => ({
  hidden: { opacity: 0 },
  visible: dependsOn ? { opacity: 1 } : { opacity: 0 },
});

const fadeUpVariant = (dependsOn: boolean) => ({
  hidden: { opacity: 0, marginTop: 20 },
  visible: dependsOn
    ? { opacity: 1, marginTop: 0 }
    : { opacity: 0, marginTop: 20 },
});

export const AccordionOnMediaBlock: React.FC<AccordionOnMediaBlockProps> = (
  props
) => {
  const {
    backgroundColor,
    content,
    heading,
    headingAppearance,
    image,
    individualSpacing,
    items,
    label,
    spacing,
    spacingBottom,
    spacingTop,
  } = props;

  const [animationDone, setAnimationDone] = React.useState(false);

  if (!items || !Array.isArray(items) || items.length === 0 || !image) {
    return null;
  }

  return (
    <BlockSettings
      backgroundColor={backgroundColor}
      individualSpacing={individualSpacing}
      spacing={spacing}
      spacingBottom={spacingBottom}
      spacingTop={spacingTop}
    >
      <div className={styles.wrapper}>
        <div className={cn(styles.container, 'container')}>
          <motion.div
            className={styles.content}
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            style={{ willChange: 'opacity, margin-top' }}
            variants={fadeUpVariant(animationDone)}
          >
            {label && <div className={styles.label}>{label}</div>}
            {heading && (
              <h3 className={cn(styles.heading, headingAppearance)}>
                {heading}
              </h3>
            )}
            <RichText content={content || {}} enableGutter={false} />
            <AccordionBlock items={items} blockType={'accordion'} />
          </motion.div>
        </div>
        <motion.div
          className={styles.media}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={fadeInVariant(true)}
          style={{ willChange: 'opacity' }}
          onAnimationComplete={() => setAnimationDone(true)}
        >
          <ImageMedia aspectRatio='landscape' image={image} />
        </motion.div>
      </div>
    </BlockSettings>
  );
};
